import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import draftService from '../services/draftService';
import Navbar from '../components/Navbar';
import usePagination from '../hooks/usePagination';
import TablePagination from '../components/TablePagination';

const MyDrafts = () => {
    const navigate = useNavigate();
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [filterFormType, setFilterFormType] = useState('');
    const [filterEquipmentType, setFilterEquipmentType] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchDrafts();
    }, []);

    const fetchDrafts = async () => {
        try {
            setLoading(true);
            const response = await draftService.getAllDrafts();
            if (response.success) {
                setDrafts(response.drafts);
            }
        } catch (err) {
            setError(err.message || 'Failed to load drafts');
        } finally {
            setLoading(false);
        }
    };

    const handleResume = async (draftId) => {
        try {
            const response = await draftService.getDraft(draftId);
            if (response.success) {
                const draft = response.draft;
                // Navigate to DynamicForm with draft data
                navigate('/inspection-form/fill', {
                    state: {
                        templateId: draft.template_id,
                        formType: {
                            form_type_id: draft.form_type_id,
                            type_name: draft.form_type_name,
                            type_code: draft.form_type_prefix
                        },
                        equipmentType: {
                            equipment_type_id: draft.equipment_type_id,
                            equipment_name: draft.equipment_type_name,
                            equipment_code: draft.equipment_type_prefix
                        },
                        draftData: draft,
                        // Pass selected inspector from draft
                        selectedInspector: draft.inspector_id ? {
                            inspector_id: draft.inspector_id,
                            inspector_name: draft.inspector_name,
                            inspector_code: draft.inspector_code
                        } : null
                    }
                });
            }
        } catch (err) {
            setError(err.message || 'Failed to load draft');
        }
    };

    const handleDelete = async (draftId, draftName) => {
        if (!window.confirm(`Are you sure you want to delete draft "${draftName}"?`)) {
            return;
        }

        try {
            const response = await draftService.deleteDraft(draftId);
            if (response.success) {
                setSuccessMessage('Draft deleted successfully');
                fetchDrafts(); // Refresh list
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (err) {
            setError(err.message || 'Failed to delete draft');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Filter drafts
    const filteredDrafts = drafts.filter(draft => {
        const matchesFormType = !filterFormType || draft.form_type_name === filterFormType;
        const matchesEquipmentType = !filterEquipmentType || draft.equipment_type_name === filterEquipmentType;
        const matchesSearch = !searchQuery ||
            draft.draft_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (draft.client_name && draft.client_name.toLowerCase().includes(searchQuery.toLowerCase()));

        return matchesFormType && matchesEquipmentType && matchesSearch;
    });

    const { page, setPage, totalPages, paginatedItems: paginatedDrafts } = usePagination(filteredDrafts);

    useEffect(() => {
        setPage(1);
    }, [filterFormType, filterEquipmentType, searchQuery, setPage]);

    // Get unique form types and equipment types for filters
    const formTypes = [...new Set(drafts.map(d => d.form_type_name))];
    const equipmentTypes = [...new Set(drafts.map(d => d.equipment_type_name))];

    return (
        <div>
            <Navbar />
            <div className="container mt-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>
                        <i className="fas fa-save me-2"></i>
                        My Drafts
                    </h2>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/new-inspection')}
                    >
                        <i className="fas fa-plus me-2"></i>
                        New Inspection Form
                    </button>
                </div>

                {error && (
                    <div className="alert alert-danger alert-dismissible fade show">
                        {error}
                        <button type="button" className="btn-close" onClick={() => setError('')}></button>
                    </div>
                )}

                {successMessage && (
                    <div className="alert alert-success alert-dismissible fade show">
                        {successMessage}
                        <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
                    </div>
                )}

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search drafts..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <select
                                    className="form-select"
                                    value={filterFormType}
                                    onChange={(e) => setFilterFormType(e.target.value)}
                                >
                                    <option value="">All Form Types</option>
                                    {formTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <select
                                    className="form-select"
                                    value={filterEquipmentType}
                                    onChange={(e) => setFilterEquipmentType(e.target.value)}
                                >
                                    <option value="">All Equipment Types</option>
                                    {equipmentTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : filteredDrafts.length === 0 ? (
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted">No drafts found</h5>
                            <p className="text-muted">
                                {drafts.length === 0
                                    ? "You haven't saved any drafts yet."
                                    : "No drafts match your filters."}
                            </p>
                            <button
                                className="btn btn-primary mt-3"
                                onClick={() => navigate('/new-inspection')}
                            >
                                Create New Inspection Form
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="card">
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>Draft Name</th>
                                            <th>Form Type</th>
                                            <th>Equipment Type</th>
                                            <th>Client</th>
                                            <th>Last Updated</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedDrafts.map(draft => (
                                            <tr key={draft.draft_id}>
                                                <td>
                                                    <strong>{draft.draft_name}</strong>
                                                </td>
                                                <td>
                                                    <span className="badge bg-primary">
                                                        {draft.form_type_name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge bg-success">
                                                        {draft.equipment_type_name}
                                                    </span>
                                                </td>
                                                <td>{draft.client_name || '-'}</td>
                                                <td>{formatDate(draft.updated_at)}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-primary me-2"
                                                        onClick={() => handleResume(draft.draft_id)}
                                                        title="Resume"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleDelete(draft.draft_id, draft.draft_name)}
                                                        title="Delete"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <TablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                            </div>
                        </div>
                        <div className="card-footer">
                            <small className="text-muted">
                                Showing {filteredDrafts.length} of {drafts.length} draft(s)
                            </small>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyDrafts;
